import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { cx } from '@/utils';
import { useJsApiLoader } from '@react-google-maps/api';
import { env } from 'env.mjs';
import moment from 'moment';
import { FaFacebookF, FaLinkedinIn } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { MdCheck, MdContentCopy, MdEmail } from 'react-icons/md';
import ReactPlayer from 'react-player';

import { formatDate } from '@/utils/dateHelpers';
import Breadcrumbs from '@/components/common/Breadcrumbs';
import SvgCalendarDay from '@/components/common/Icons/CalendarDay';
import { SvgCommunity } from '@/components/common/Icons/Community';
import SvgEmail from '@/components/common/Icons/Email';
import { SvgLogout } from '@/components/common/Icons/Logout';
import SvgMapMarker from '@/components/common/Icons/MapMarker';
import SvgPhone from '@/components/common/Icons/Phone';
import SvgTimer from '@/components/common/Icons/Timer';
import { LexicalViewer } from '@/components/common/LexicalEditorComponent/LexicalViewer';
import Modal from '@/components/common/Modals/Modal';
import {
  EventPaymentType,
  EventsItem,
  MultimediaTypes,
} from '@/components/common/Providers/GraphqlProvider/generated';
import { getEventStatus } from '@/components/common/Utils/utils';

import { EventDetailInMap } from './EventDetailInMap';
import { EventItemMoreEvents } from './EventItemMoreEvents';
import { EventItemSocialNetworks } from './EventItemSocialNetworks';

const MarkdownPreview = dynamic(() => import('@uiw/react-markdown-preview'), {
  ssr: false,
});

const ticketTypeLabel: Record<EventPaymentType, string> = {
  [EventPaymentType.Free]: 'Gratis',
  [EventPaymentType.Paid]: 'Con costo',
};

type EventItemProps = {
  eventData?: EventsItem | null;
};

export const EventItem = ({ eventData }: EventItemProps) => {
  const router = useRouter();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: env.NEXT_PUBLIC_GOOGLE_MAP_KEY,
  });
  const [center, setCenter] = useState<google.maps.LatLngLiteral>();
  const zoom = 18;
  const [clicks, setClicks] = useState<google.maps.LatLng>();
  const [mapOpended, setMapOpened] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (eventData && eventData?.latitude && eventData?.longitude && isLoaded) {
      setCenter({
        lat: Number(eventData.latitude),
        lng: Number(eventData.longitude),
      });

      try {
        const location = new google.maps.LatLng({
          lat: Number(eventData.latitude),
          lng: Number(eventData.longitude),
        });
        setClicks(location);
      } catch (e) {
        try {
          const location = new google.maps.LatLng({
            lat: Number(eventData.latitude),
            lng: Number(eventData.longitude),
          });
          setClicks(location);
        } catch (err) {}
      }
    }
  }, [isLoaded, eventData]);

  const openMap = () => {
    setMapOpened(true);
  };

  const closeMap = () => {
    setMapOpened(false);
  };

  // Construir URL para compartir usando slug si está disponible
  const getShareUrl = (): string => {
    if (typeof window === 'undefined') return '';
    const origin = window.location.origin;
    const slug = eventData?.seo?.metadata?.[0]?.slug;
    const identifier = slug || eventData?.id;
    return `${origin}/events/${identifier}`;
  };

  const shareUrl = getShareUrl();
  const shareTitle = eventData?.eventItemTitle || 'Evento';
  const shareDescription = eventData?.eventItemDescription || '';

  // Funciones de compartir
  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareOnTwitter = () => {
    const text = shareTitle;
    const hashtags = eventData?.eventItemTags?.join(',') || '';
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}&hashtags=${encodeURIComponent(hashtags)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareOnLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`${shareTitle}\n\n${shareDescription}\n\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const copyToClipboard = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };

  if (!eventData) {
    return null;
  }

  return (
    <div className="mt-8">
      <div className="inner-extend">
        <Breadcrumbs
          items={[
            { name: 'Eventos', route: '/events' },
            { name: 'Evento', route: '#' },
          ]}
          color="text-tundora"
          colorLink="text-dimGray"
        />

        <div className="mt-14 flex gap-10">
          <div className="flex flex-[0.7] flex-col gap-[20px]">
            <p className="font-montserrat text-[28px] font-semibold leading-[33.6px] text-primary">
              <LexicalViewer value={eventData.eventItemTitle} />
            </p>
            <div className="flex justify-between">
              <p className="font-montserrat text-[20px] font-semibold italic leading-[30px] text-[#737373]">
                {moment.utc(eventData.startDate).format('DD/MM/YYYY h:mm a')}
              </p>
              <div className="flex gap-3">
                <ShareButton
                  onClick={shareByEmail}
                  icon={<MdEmail className="text-lg" />}
                  title="Compartir por email"
                />
                <ShareButton
                  onClick={shareOnFacebook}
                  icon={<FaFacebookF className="text-base" />}
                  title="Compartir en Facebook"
                />
                <ShareButton
                  onClick={shareOnTwitter}
                  icon={<FaXTwitter className="text-base" />}
                  title="Compartir en X"
                />
                <ShareButton
                  onClick={shareOnLinkedIn}
                  icon={<FaLinkedinIn className="text-base" />}
                  title="Compartir en LinkedIn"
                />
                <ShareButton
                  onClick={copyToClipboard}
                  icon={
                    isCopied ? (
                      <MdCheck className="text-lg" />
                    ) : (
                      <MdContentCopy className="text-base" />
                    )
                  }
                  title={isCopied ? 'Copiado!' : 'Copiar enlace'}
                  isActive={isCopied}
                />
              </div>
            </div>
            <div className="relative">
              {eventData.multimediaType === MultimediaTypes.Image &&
              eventData.image?.downloadUrl ? (
                <Image
                  alt="ImageEvent"
                  src={eventData.image?.downloadUrl ?? ''}
                  width={0}
                  height={0}
                  sizes="100vw"
                  style={{ width: '100%', height: 'auto' }}
                />
              ) : eventData.multimediaType === MultimediaTypes.Video && eventData.linkItem?.url ? (
                <div className="h-[450px] max-h-[450px] w-full overflow-hidden rounded-xl">
                  <ReactPlayer
                    controls
                    url={`${eventData.linkItem?.url}`}
                    width="100%"
                    height="100%"
                    muted
                  />
                </div>
              ) : null}
              <p className="mt-4 text-justify font-workSans text-[1rem] font-normal leading-[24px] text-tundora">
                <MarkdownPreview source={eventData.eventItemDescription as string} />
              </p>
            </div>
          </div>

          <div className="flex flex-[0.3] flex-col">
            <p className="mb-3 font-montserrat text-[20px] font-semibold leading-[30px] text-primary">
              Etiquetas
            </p>
            <div className="inline-flex flex-wrap gap-[10px]">
              {eventData.eventItemTags.map((tag, index) => (
                <p
                  key={index}
                  className="rounded-[20px] bg-[#F2F2F2] px-[10px] py-[6px] font-workSans text-[14px] font-semibold lowercase leading-[21.01px] text-black first-letter:capitalize"
                >
                  {tag}
                </p>
              ))}
            </div>
            <div className="mt-8 rounded-[16px] border-2 border-blueLight px-[1.5rem] py-[1rem]">
              <p className="font-montserrat text-[24px] font-semibold leading-[36px] text-primary">
                Detalles del evento
              </p>
              <hr className="border border-primary" />
              <div className="mt-[1rem] flex flex-col gap-[20px]">
                <EventInfo
                  icon={
                    <SvgCalendarDay
                      color="primaryDark"
                      className="h-[24px] w-[24px] fill-primary"
                    />
                  }
                  title="Fecha de inicio"
                  subtitle={moment.utc(eventData.startDate).format('DD/MM/YYYY h:mm a')}
                />
                <EventInfo
                  icon={
                    <SvgCalendarDay
                      color="primaryDark"
                      className="h-[24px] w-[24px] fill-primary"
                    />
                  }
                  title="Fecha de finalización"
                  subtitle={moment.utc(eventData.endDate).format('DD/MM/YYYY h:mm a')}
                />
                <EventInfo
                  icon={<SvgTimer className="h-[24px] w-[24px] text-primary" />}
                  title="Estado"
                  subtitle={getEventStatus(eventData.eventStatus)}
                />
                {eventData.address && (
                  <EventInfo
                    icon={<SvgMapMarker className="h-[24px] w-[24px] text-primary" />}
                    title="Lugar del evento"
                    subtitle={eventData.address}
                    onClick={openMap}
                  />
                )}
                {eventData?.phoneEvent && (
                  <EventInfo
                    icon={<SvgPhone className="h-[24px] w-[24px] text-primary" />}
                    title="Teléfono"
                    subtitle={eventData.phoneEvent}
                  />
                )}
                {eventData?.emailEvent && (
                  <EventInfo
                    icon={<SvgEmail className="h-[24px] w-[24px] text-primary" />}
                    title="Correo de contacto"
                    subtitle={eventData.emailEvent}
                  />
                )}
                {eventData?.groupEvent && (
                  <EventInfo
                    icon={<SvgCommunity className="h-[24px] w-[24px] text-primary" />}
                    title="Dependencia o sectorial"
                    subtitle={eventData.groupEvent}
                  />
                )}
                {eventData?.paymentTypeEvent &&
                  ticketTypeLabel[eventData.paymentTypeEvent] && (
                    <EventInfo
                      icon={<SvgLogout className="h-[24px] w-[24px] text-primary" />}
                      title="Tipo de entrada"
                      subtitle={ticketTypeLabel[eventData.paymentTypeEvent]}
                    />
                  )}
              </div>
            </div>

            <EventItemMoreEvents currentEventId={eventData.id} />

            <EventItemSocialNetworks />

            <Modal
              open={mapOpended}
              handler={setMapOpened}
              size="xl"
              className="overflow-hidden rounded-lg"
            >
              <EventDetailInMap
                center={center}
                clicks={clicks}
                eventItem={eventData as EventsItem}
                zoom={zoom}
                closeMap={closeMap}
              />
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EventInfo = ({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: any;
  title: string;
  subtitle: string;
  onClick?: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const updateHover = (value: boolean) => {
    if (onClick != undefined) {
      setIsHovered(value);
    }
  };

  return (
    <div
      className={cx('flex items-center gap-[18px]', { 'cursor-pointer': onClick != undefined })}
      onClick={onClick ? onClick : () => {}}
      onMouseEnter={() => updateHover(true)}
      onMouseLeave={() => updateHover(false)}
    >
      {icon}
      <div>
        <p className="font-montserrat text-[1rem] font-semibold leading-[1.5rem] text-tundora">
          {title}
        </p>
        <p
          className={cx('font-workSans text-[14px] leading-[16.8px] text-dimGray transition-all', {
            underline: isHovered,
          })}
        >
          {subtitle}
        </p>
      </div>
    </div>
  );
};

type ShareButtonProps = {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  isActive?: boolean;
};

const ShareButton = ({ onClick, icon, title, isActive }: ShareButtonProps) => {
  return (
    <button
      onClick={onClick}
      title={title}
      className={cx(
        'flex h-[36px] w-[36px] items-center justify-center rounded-full transition-colors',
        isActive
          ? 'bg-green-500 text-white'
          : 'bg-primaryLight text-primary hover:bg-primary hover:text-white'
      )}
    >
      {icon}
    </button>
  );
};
